import { Link } from 'react-router-dom';
import { Layout } from '../components/layout';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { ArrowRight } from 'lucide-react';
import { SEO, OrganizationSchema, ConsultingServiceSchema } from '../components/SEO';

const MURMURATION_URL = "https://customer-assets.emergentagent.com/job_transform-org-1/artifacts/9rxfpakq_c973ebb5f610f3635bd41a787e6db9b1%20%281%29.jpg";

// Service card data
const services = [
  {
    id: 'goldilocks',
    title: 'Goldilocks Zone Advisory',
    outcome: 'Your organisation gains clarity on what\'s enabling and inhibiting change — and a path toward productive balance.',
    description: 'Through observation, interviews, and organisational diagnostics, we assess whether your environment supports transformation. We identify where systems are too rigid, too fragmented, or poorly aligned — and design interventions that move toward the Goldilocks Zone.'
  },
  {
    id: 'operating-model',
    title: 'Operating Model Design',
    outcome: 'Your structure, governance, and decision rights align with where you\'re headed — not where you\'ve been.',
    description: 'We work with leadership teams to redesign operating models that align accountability, decision rights, and collaboration patterns with strategic intent. The goal is clarity without rigidity — enabling speed while maintaining enterprise coherence.'
  },
  {
    id: 'transformation',
    title: 'Transformation Assurance',
    outcome: 'Your leadership maintains genuine visibility over transformation health — not just delivery activity.',
    description: 'We provide independent assurance to assess programme health, governance effectiveness, delivery capability, and strategic alignment. This includes identifying systemic risks and strengthening decision frameworks so the transformation remains meaningful.'
  },
  {
    id: 'delivery',
    title: 'Delivery System Optimisation',
    outcome: 'Your teams can move quickly, collaborate effectively, and adapt as priorities evolve.',
    description: 'We design delivery systems that balance autonomy with alignment — improving planning structures, decision pathways, team interfaces, and feedback loops so work flows effectively across the organisation.'
  },
  {
    id: 'ai-readiness',
    title: 'AI Readiness & Adoption',
    outcome: 'Your organisation adopts AI in a way that augments your people and strengthens your operating model, rather than disrupting both.',
    description: 'We help leaders navigate AI adoption as an organisational change programme — assessing workforce readiness, redesigning workflows, and ensuring technology strengthens how your organisation operates rather than hollowing it out.'
  }
];

// Client logos with actual images and custom sizes for visual balance
const clientLogos = [
  { 
    name: 'Australia Post', 
    id: 'australia-post',
    logo: 'https://customer-assets.emergentagent.com/job_transform-org-1/artifacts/g6i3scte_AustraliaPost.png',
    height: '40px'
  },
  { 
    name: 'Telstra', 
    id: 'telstra',
    logo: 'https://customer-assets.emergentagent.com/job_transform-org-1/artifacts/l7gj3htl_Telstra.png',
    height: '35px'
  },
  { 
    name: 'NAB', 
    id: 'nab',
    logo: 'https://customer-assets.emergentagent.com/job_transform-org-1/artifacts/5xqzskcf_NAB.png',
    height: '35px'
  },
  { 
    name: 'Myer', 
    id: 'myer',
    logo: 'https://customer-assets.emergentagent.com/job_transform-org-1/artifacts/edna4fj7_Myer_Logo.svg.png',
    height: '22px'
  },
  { 
    name: 'MLC', 
    id: 'mlc',
    logo: 'https://customer-assets.emergentagent.com/job_transform-org-1/artifacts/7balfaw7_mlc%20logo.svg',
    height: '38px'
  },
  { 
    name: 'Suncorp', 
    id: 'suncorp',
    logo: 'https://customer-assets.emergentagent.com/job_transform-org-1/artifacts/q3wlgt6c_Suncorp.png',
    height: '55px'
  },
  { 
    name: 'ME Bank', 
    id: 'me-bank',
    logo: 'https://customer-assets.emergentagent.com/job_transform-org-1/artifacts/w83ara1o_Me%20Bank%20logo.png',
    height: '40px'
  },
  { 
    name: 'Energy Australia', 
    id: 'energy-australia',
    logo: 'https://customer-assets.emergentagent.com/job_transform-org-1/artifacts/ltkxp0xf_EnergyAustralia.png',
    height: '50px'
  },
  { 
    name: 'Australian Unity', 
    id: 'australian-unity',
    logo: 'https://customer-assets.emergentagent.com/job_transform-org-1/artifacts/80cnhrt9_Australian_Unity_Logo.png',
    height: '45px'
  }
];

// Service Card Component
const ServiceCard = ({ service, index }) => {
  const [ref, isVisible] = useScrollReveal(0.1);
  
  return (
    <Link 
      to={`/services#${service.id}`}
      ref={ref}
      data-testid={`service-card-${service.id}`}
      className={`service-card bg-white border border-[#e5e2dc] p-8 transition-all duration-500 hover:border-[#d4782c] hover:shadow-lg cursor-pointer block ${
        isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
      }`}
      style={{ transitionDelay: `${index * 100}ms` }}
    >
      <h3 
        className="font-playfair font-bold text-[#1a1a1a] mb-4"
        style={{ fontSize: '23px' }}
      >
        {service.title}
      </h3>
      <p 
        className="font-playfair italic text-[#d4782c] mb-4"
        style={{ fontSize: '16px' }}
      >
        {service.outcome}
      </p>
      <p 
        className="text-[#4a4744]"
        style={{ fontSize: '15px', lineHeight: '1.75' }}
      >
        {service.description}
      </p>
    </Link>
  );
};

// Section Components
const HeroSection = () => {
  return (
    <section 
      className="min-h-screen relative flex items-center bg-white overflow-hidden"
      data-testid="hero-section"
    >
      {/* Murmuration Background - Right side */}
      <div 
        className="absolute inset-y-0 right-0 w-full md:w-[55%]"
        style={{
          backgroundImage: `url(${MURMURATION_URL})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          opacity: 0.18,
          filter: 'grayscale(100%) contrast(1.1)',
          maskImage: 'linear-gradient(to right, transparent 0%, black 40%)',
          WebkitMaskImage: 'linear-gradient(to right, transparent 0%, black 40%)'
        }}
      />
      
      {/* Content */}
      <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10 w-full">
        <div className="max-w-2xl">
          <h1 
            className="font-playfair text-4xl sm:text-5xl lg:text-6xl font-bold italic text-[#1a1a1a] mb-6 animate-fade-in-up"
            data-testid="hero-headline"
          >
            Creating the Conditions for Meaningful Change
          </h1>
          <p 
            className="font-dm-sans text-lg text-[#4a4744] mb-10 leading-relaxed animate-fade-in-up animate-delay-200"
            data-testid="hero-subtitle"
          >
            Agilitie is a boutique advisory firm helping organisations redesign operating models, leadership systems, and delivery environments so meaningful change can take hold.
          </p>
          <div className="flex flex-wrap gap-4 animate-fade-in-up animate-delay-300">
            <Link to="/services" className="btn-primary" data-testid="hero-cta-services">
              Explore Services
            </Link>
            <Link to="/contact" className="btn-secondary" data-testid="hero-cta-contact">
              Contact Us
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
};

const ServicesSection = () => {
  const [ref, isVisible] = useScrollReveal(0.1);

  return (
    <section 
      className="py-24 bg-[#faf9f7]"
      data-testid="services-section"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Section Header */}
        <div 
          ref={ref}
          className={`max-w-3xl mb-16 transition-all duration-700 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <p className="text-[#d4782c] font-dm-sans font-semibold text-sm uppercase tracking-wider mb-4">
            HOW WE HELP ORGANISATIONS CHANGE
          </p>
          <h2 className="font-playfair text-3xl md:text-4xl font-bold text-[#1a1a1a] mb-4">
            Building your organisation's capacity for change
          </h2>
          <p className="text-[#4a4744] text-lg leading-relaxed">
            Every engagement is designed to strengthen your organisation's own capability — so lasting change comes from within.
          </p>
        </div>

        {/* Service Cards Grid - 3 columns on large screens for 5 cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {services.slice(0, 3).map((service, index) => (
            <ServiceCard key={service.id} service={service} index={index} />
          ))}
        </div>
        {/* Bottom row - 2 cards centered */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mt-5 lg:max-w-[66.666%] lg:mx-auto">
          {services.slice(3).map((service, index) => (
            <ServiceCard key={service.id} service={service} index={index + 3} />
          ))}
        </div>
      </div>
    </section>
  );
};

const MurmurationDivider = () => {
  const [ref, isVisible] = useScrollReveal(0.2);

  return (
    <section 
      ref={ref}
      className="relative h-[200px] bg-[#1a1a1a] flex items-center justify-center overflow-hidden"
      data-testid="murmuration-divider"
    >
      {/* Background Image */}
      <div 
        className="absolute inset-0"
        style={{
          backgroundImage: `url(${MURMURATION_URL})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          opacity: 0.25,
          filter: 'grayscale(100%)'
        }}
      />
      
      {/* Quote */}
      <blockquote 
        className={`relative z-10 max-w-4xl mx-auto px-6 text-center transition-all duration-700 ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
        }`}
      >
        <p className="font-playfair italic text-white text-lg md:text-xl lg:text-2xl leading-relaxed">
          "Thousands of individuals moving in perfect coherence — without central command. This is the transformation we help organisations realise."
        </p>
      </blockquote>
    </section>
  );
};

const EthosSection = () => {
  const [ref, isVisible] = useScrollReveal(0.1);

  return (
    <section 
      className="py-24 bg-white"
      data-testid="ethos-section"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div 
          ref={ref}
          className={`grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 transition-all duration-700 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          {/* Left Column */}
          <div className="relative">
            <div className="border-l-[3px] border-[#d4782c] pl-8">
              <p className="text-[#d4782c] font-dm-sans font-semibold text-sm uppercase tracking-wider mb-4">
                OUR ETHOS
              </p>
              <h2 className="font-playfair text-3xl md:text-4xl font-bold italic text-[#1a1a1a] leading-tight">
                The measure of our success is the moment you no longer need us.
              </h2>
            </div>
          </div>

          {/* Right Column */}
          <div className="space-y-6">
            <p className="text-[#4a4744] leading-relaxed">
              Engagements are led by founder Nadir Khan and supported by a trusted network of specialists with deep experience across technology, delivery, and organisational design.
            </p>
            <p className="text-[#4a4744] leading-relaxed">
              We believe the most valuable thing we can do for a client is make ourselves redundant. Every engagement is designed to uplift your internal capability and build change momentum from within — so your organisation becomes self-reliant, self-sustaining, and self-sufficient.
            </p>
            
            {/* Callout Box */}
            <div className="bg-[#faf9f7] border-l-[3px] border-[#d4782c] p-6 mt-8">
              <p className="text-[#4a4744] leading-relaxed">
                <strong className="text-[#1a1a1a]">Our commitment:</strong> We work aggressively toward transferring capability into your teams. We build the conditions, frameworks, and confidence your people need to lead change themselves. The goal isn't a long-term advisory relationship — it's an organisation that doesn't need one.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

const GoldilocksTeaser = () => {
  const [ref, isVisible] = useScrollReveal(0.1);
  const BOOK_COVER_URL = "https://customer-assets.emergentagent.com/job_87621aa9-388b-4a4a-b140-4ad09ad180a4/artifacts/5y2acst5_4eea1690-43b4-468c-a2dd-3248c467d977.png";

  return (
    <section 
      className="py-24 bg-[#faf9f7]"
      data-testid="goldilocks-teaser"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div 
          ref={ref}
          className={`grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8 items-center transition-all duration-700 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          {/* Book Cover - Left */}
          <div className="flex justify-center lg:justify-start lg:pl-[25%]">
            <div 
              className="max-w-xs"
              data-testid="book-cover-container"
            >
              <img 
                src={BOOK_COVER_URL} 
                alt="The Goldilocks Zone - How Leaders Build the Conditions for Continuous Evolution by Nadir Khan & Craig Kimball"
                className="w-full shadow-2xl"
              />
            </div>
          </div>

          {/* Content - Right */}
          <div>
            <p className="text-[#d4782c] font-dm-sans font-semibold text-sm uppercase tracking-wider mb-4">
              THE GOLDILOCKS ZONE
            </p>
            <h2 className="font-playfair text-2xl md:text-3xl font-bold italic text-[#1a1a1a] mb-6 leading-tight">
              Organisations don't fail at change because of poor strategy. They fail because the environment makes success nearly impossible.
            </h2>
            <p className="text-[#4a4744] leading-relaxed mb-8">
              Our book and advisory practice are built on a single insight: leaders should manage the environment for change, not change itself. When the conditions are right, transformation emerges naturally.
            </p>
            <Link 
              to="/the-goldilocks-zone" 
              className="inline-flex items-center gap-2 text-[#d4782c] font-dm-sans font-semibold hover:gap-4 transition-all duration-300"
              data-testid="goldilocks-link"
            >
              Explore the Goldilocks Zone
              <ArrowRight size={18} />
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
};

const TrustedBySection = () => {
  const [ref, isVisible] = useScrollReveal(0.1);

  return (
    <section 
      className="py-20 bg-white"
      data-testid="trusted-by-section"
    >
      <div className="max-w-5xl mx-auto px-6 lg:px-8">
        <p 
          ref={ref}
          className={`text-center text-[#4a4744] font-dm-sans font-medium text-sm uppercase tracking-wider mb-12 transition-all duration-700 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
          }`}
        >
          TRUSTED BY LEADERS ACROSS AUSTRALIA'S LARGEST ORGANISATIONS
        </p>
        
        {/* Client Logos */}
        <div className="flex flex-wrap justify-center items-center gap-x-10 gap-y-8">
          {clientLogos.map((client) => (
            <img 
              key={client.id}
              src={client.logo}
              alt={client.name}
              data-testid={`client-logo-${client.id}`}
              style={{ height: client.height }}
              className="w-auto object-contain transition-all duration-300 filter grayscale opacity-60 hover:grayscale-0 hover:opacity-100"
            />
          ))}
        </div>
      </div>
    </section>
  );
};

const CTASection = () => {
  const [ref, isVisible] = useScrollReveal(0.1);

  return (
    <section 
      className="py-24 bg-[#faf9f7]"
      data-testid="cta-section"
    >
      <div className="max-w-3xl mx-auto px-6 lg:px-8 text-center">
        <div 
          ref={ref}
          className={`transition-all duration-700 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <h2 className="font-playfair text-3xl md:text-4xl font-bold text-[#1a1a1a] mb-4">
            Ready to create the conditions for meaningful change?
          </h2>
          <p className="text-[#4a4744] text-lg mb-10">
            We'd love to understand your challenges and explore how we can help.
          </p>
          <Link 
            to="/contact" 
            className="btn-primary inline-block"
            data-testid="cta-button"
          >
            Start a Conversation
          </Link>
        </div>
      </div>
    </section>
  );
};

// Main HomePage Component
export default function HomePage() {
  return (
    <Layout>
      <SEO 
        path="/"
        description="Boutique advisory helping Australian organisations redesign operating models, leadership systems, and delivery environments for meaningful, lasting change."
      />
      <OrganizationSchema />
      <ConsultingServiceSchema />
      <HeroSection />
      <ServicesSection />
      <MurmurationDivider />
      <EthosSection />
      <GoldilocksTeaser />
      <TrustedBySection />
      <CTASection />
    </Layout>
  );
}
